/* ============================================================
   Fence Works — crash reporting (→ FW Admin → Ops & Health → App errors)

   Until this, nothing reported a front-end crash: we heard about one only
   if a contractor told us. What is caught:
     error               window.onerror — an uncaught exception anywhere
     unhandledrejection  a promise that failed with nobody listening
     boundary            a React screen that failed to render (PageErrorBoundary
                         in app.tsx, and the top-level card in lib/crashcard.tsx)

   What is sent (POST /client-errors, server/src/clienterrors.js): the message,
   the stack (the service source-maps it when the build's maps are on the
   box), the release, the route (the screen's name only, never a token or a
   query), the browser and OS, and the time. Workspace and user ids come from
   the session on the server — never from here — and are ids only.

   What is NEVER sent: form values (nothing here reads the page), quote
   contents, customer names, emails, phone numbers, tokens, or a URL's query
   string. scrub() takes them out of the message and the stack before a report
   leaves, and the server does it again. Names the app holds (customers, the
   company, the signed-in user) are taken out too — app.tsx hands them over
   with setCrashRedactor().

   Limits: the same fingerprint once per session, at most 10 reports per
   session, and nothing at all in the demo sandbox or without the service.

   What is NOT reported — a browser talking about its own state, not about
   our code (isNoise, isThirdParty; the server keeps the same list):
     "ResizeObserver loop …"        the observer ran again inside one frame
     "Script error." with no stack  a cross-origin script: nothing to read
     "The provided callback is no longer runnable."
         Chrome refusing to run a JS callback (a ResizeObserver, a rAF, a
         listener) because the page, or the frame that scheduled it, is
         paused or already gone — it surfaces inside window.print(), whose
         print dialog runs a nested loop on the page, and when an iframe is
         torn down. The callback is skipped, not broken. It reads as "Failed
         to execute 'print' on 'Window': …" when print() is on the stack.
     "Promise was rejected because the browsing context is going away"
         WebKit tearing a page down (a reload, leaving, Safari reclaiming
         the tab) and rejecting a pending navigator.permissions.query() on
         the way out.
     a stack whose every frame is someone else's script — the tag manager's
         tags, the pixel, a browser extension. That code runs on our page
         but isn't ours to fix, and it can't be read here anyway.
   ============================================================ */

declare const __FW_RELEASE__: string | undefined;

export type CrashKind = "error" | "unhandledrejection" | "boundary";
export const MAX_PER_SESSION = 10;
const STORE_KEY = "fw_crash_sent";

/* The build this bundle is (vite.config.ts): its UTC build time, then its commit. */
export const RELEASE: string = (typeof __FW_RELEASE__ !== "undefined" && __FW_RELEASE__) || "dev";

/* ============================================================
   scrub — the same rules as server/src/clienterrors.js scrubText.
   (No lookbehind anywhere: Safari before 16.4 can't parse it, and a regex
   the browser can't parse would take the whole app down with it.)
   ============================================================ */
const CODE_TOKEN = /^[a-z_$][\w$.]{0,39}$/;
const LEFT_EDGE = /[\s("'`=,[{]/;
const tailOf = (q: string) => (q.match(/(?::\d+){1,2}$/) || [""])[0];

export function scrub(input: any, redact: string[] = []): string {
  let s = String(input == null ? "" : input);
  const words = Array.from(new Set((redact || []).map((x) => String(x || "").trim()).filter((x) => x.length >= 3))).sort((a, b) => b.length - a.length);
  for (const w of words) s = s.split(w).join("[redacted]");
  s = s.replace(/\bdata:[a-z0-9.+/-]*[;,][^\s"'`<>()]*/gi, "data:[…]");
  s = s.replace(/\b((?:https?|wss?|file|chrome-extension|moz-extension|safari-web-extension):\/\/[^\s?#"'`<>()]*)([?#][^\s"'`<>()]*)/gi,
    (_m, base, q) => base + "?[…]" + tailOf(q));
  s = s.replace(/(^|[\s("'`=,])(\/[\w\-./%~]*)([?#][^\s"'`<>()]+)/g, (_m, pre, p, q) => pre + p + "?[…]" + tailOf(q));
  s = s.replace(/[A-Za-z0-9._%+'-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g, "[email]");
  s = s.replace(/\beyJ[\w-]{4,}\.[\w-]{4,}\.[\w-]{4,}/g, "[token]");
  s = s.replace(/\b(bearer|basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi, (_m, k) => k + " [redacted]");
  s = s.replace(/\b(token|access_token|refresh_token|id_token|api[_-]?key|apikey|secret|client_secret|password|passwd|pwd|authorization|session|sig|signature|code)(\s*[:=]\s*)(?!\[)[^\s&"'`,;)}\]]+/gi, (_m, k, sep) => k + sep + "[redacted]");
  s = s.replace(/(^|[^\w-])((?:sk|pk|rk|whsec|cs|pi|seti|cus|sub|acct|in|ch|tok|pm)_(?:live|test)_[A-Za-z0-9]{6,}|[A-Za-z0-9_-]{32,})(?![\w-])/g, (m, pre, tok) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tok) ? m : pre + "[token]");
  s = s.replace(/(^|[^\d:.])\d(?:[ -]?\d){11,18}(?!\d)/g, (_m, pre) => pre + "[number]");
  s = s.replace(/(^|[^\d:.])(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)/g, (_m, pre) => pre + "[phone]");
  s = s.replace(/(["'`])([^"'`\n]{0,400}?)\1/g, (m, q, body, at, all) => {
    const before = at > 0 ? all[at - 1] : " ";
    if (at > 0 && !LEFT_EDGE.test(before)) return m;   // an apostrophe inside a word
    return CODE_TOKEN.test(body) || body === "" || body === "[…]" ? m : q + "…" + q;
  });
  return s;
}

/* The strings this account holds that must never leave in a report: every
   customer's name, email and phone, the company, the owner, the signed-in
   user. Capped; short ones (initials) are skipped by scrub(). */
export function sensitiveStrings(state: any, user?: any): string[] {
  const out: string[] = [];
  const add = (...xs: any[]) => { for (const x of xs) if (x && typeof x === "string" && out.length < 3000) out.push(x); };
  const st = state || {};
  for (const c of (st.customers || []).slice(0, 2000)) if (c) add(c.name, c.email, c.phone, c.address, c.street);
  const co = st.company || {};
  add(co.name, co.email, co.phone, co.address);
  const se = st.settings || {};
  add(se.companyOwner, se.ownerEmail, se.companyName);
  if (user) add(user.name, user.email);
  return out;
}

let redactor: (() => string[]) | null = null;
export function setCrashRedactor(fn: (() => string[]) | null) { redactor = fn; }
const redactList = (): string[] => { try { return redactor ? redactor() || [] : []; } catch { return []; } };

/* ============================================================
   Where and on what
   ============================================================ */
export function browserOf(ua: string): string {
  const m = (re: RegExp) => { const x = re.exec(ua); return x ? x[1] : ""; };
  if (/Edg\//.test(ua)) return "Edge " + m(/Edg\/(\d+)/);
  if (/SamsungBrowser\//.test(ua)) return "Samsung Internet " + m(/SamsungBrowser\/(\d+)/);
  if (/(?:OPR|Opera)\//.test(ua)) return "Opera " + m(/(?:OPR|Opera)\/(\d+)/);
  if (/Firefox\/|FxiOS\//.test(ua)) return "Firefox " + m(/(?:Firefox|FxiOS)\/(\d+)/);
  if (/CriOS\//.test(ua)) return "Chrome " + m(/CriOS\/(\d+)/);
  if (/Chrome\//.test(ua)) return "Chrome " + m(/Chrome\/(\d+)/);
  if (/Safari\//.test(ua) && /Version\//.test(ua)) return "Safari " + m(/Version\/(\d+)/);
  return "Other";
}
export function osOf(ua: string): string {
  return /iPhone|iPad|iPod/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /CrOS/.test(ua) ? "ChromeOS"
    : /Mac OS X|Macintosh/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "Other";
}
export const deviceOf = (w: number) => (w < 700 ? "phone" : w < 1100 ? "tablet" : "desktop");
/* The screen, by name: the first path segment ("/estimator?loadProject=p1" →
   "estimator", "/hub/<token>" → "hub"). Never the rest of the path. */
export function routeOf(pathname: string): string {
  const seg = String(pathname || "/").split(/[?#]/)[0].split("/").filter(Boolean)[0] || "dashboard";
  return /^[\w-]{1,40}$/.test(seg) ? seg : "other";
}

/* ============================================================
   The report, and whether to send it
   ============================================================ */
/* Noise a browser raises that says nothing about our code. */
export function isNoise(message: string, stack: string): boolean {
  if (/^ResizeObserver loop/i.test(message)) return true;
  if (/^Script error\.?$/i.test(message) && !stack) return true;   // a cross-origin script: no message, no stack, nothing to fix
  // Chrome, for a JS callback (a ResizeObserver, a rAF, a listener) it won't
  // run because the page, or the frame that scheduled it, is paused or gone:
  // inside window.print()'s nested dialog loop, when an iframe is torn down.
  // The callback is skipped, not broken. Reads as "Failed to execute 'print'
  // on 'Window': …" when print() is on the stack.
  if (/The provided callback is no longer runnable/i.test(message)) return true;
  // WebKit tearing the page down (a reload, leaving, Safari reclaiming the
  // tab) and rejecting a pending navigator.permissions.query() on the way out
  if (/browsing context is going away/i.test(message)) return true;
  return false;
}

/* Every frame of the stack (or the failing script itself, when the error
   event names one and there is no stack) is on another origin: the tag
   manager's tags, the pixel, a browser extension. That code runs on our page
   but isn't ours to fix. A stack with no URL in it at all (Safari's
   DOMExceptions, "<anonymous>") can't be told apart, so it is kept. */
const ORIGIN_RE = /\b[a-z][a-z0-9+.-]*:\/\/[^\s/?#)"'`]+/gi;
export function isThirdParty(stack: string, fallbackFrame: string | null | undefined, origin: string): boolean {
  const ours = String(origin || "").toLowerCase();
  if (!ours) return false;
  const seen = (String(stack || "") + "\n" + String(fallbackFrame || "")).match(ORIGIN_RE) || [];
  if (!seen.length) return false;
  return seen.every((o) => o.toLowerCase() !== ours);
}

/* this session's key for "the same error": the scrubbed message with its
   numbers taken out, and the first frames (djb2, hex) */
export function clientFingerprint(message: string, stack: string): string {
  const frames = String(stack || "").split("\n").map((l) => l.trim()).filter((l) => /^at |@/.test(l)).slice(0, 3);
  const basis = String(message || "").replace(/\d+/g, "N") + "|" + frames.join("|");
  let h = 5381;
  for (let i = 0; i < basis.length; i++) h = ((h * 33) ^ basis.charCodeAt(i)) >>> 0;
  return h.toString(16);
}

export type SentState = { sent: string[]; count: number };
/* once per fingerprint, at most MAX_PER_SESSION in all → { send, next } */
export function decide(state: SentState, fp: string): { send: boolean; next: SentState } {
  const st = { sent: (state && state.sent) || [], count: (state && state.count) || 0 };
  if (st.count >= MAX_PER_SESSION || st.sent.includes(fp)) return { send: false, next: st };
  return { send: true, next: { sent: [...st.sent, fp].slice(-50), count: st.count + 1 } };
}

export type Env = { ua: string; pathname: string; width: number; now: number };
/* An error (or anything thrown) → the report. Pure, given the environment. */
export function buildReport(kind: CrashKind, err: any, extra: { componentStack?: string | null; fallbackFrame?: string | null } = {}, env: Env, redact: string[] = []) {
  const e = err || {};
  const raw = e instanceof Error || (typeof e === "object" && ("message" in e || "stack" in e))
    ? String((e as any).message || (e as any).name || "Error")
    : typeof e === "string" ? e : "Non-error value thrown";
  const name = e && typeof (e as any).name === "string" && (e as any).name !== "Error" && !raw.startsWith((e as any).name) ? (e as any).name + ": " : "";
  let stack = typeof (e as any).stack === "string" ? (e as any).stack : "";
  if (!stack && extra.fallbackFrame) stack = raw + "\n    at " + extra.fallbackFrame;
  return {
    kind,
    message: scrub(name + raw, redact).slice(0, 500),
    stack: scrub(stack, redact).slice(0, 8000),
    componentStack: extra.componentStack ? scrub(extra.componentStack, redact).slice(0, 3000) : null,
    release: RELEASE,
    route: routeOf(env.pathname),
    browser: browserOf(env.ua),
    os: osOf(env.ua),
    device: deviceOf(env.width),
    at: new Date(env.now).toISOString(),
  };
}

/* ============================================================
   Wiring (the only part that touches the browser)
   ============================================================ */
let memState: SentState = { sent: [], count: 0 };
const store = (): Storage | null => { try { return window.sessionStorage; } catch { return null; } };
function readState(): SentState {
  try { const raw = store() && store()!.getItem(STORE_KEY); if (raw) { const s = JSON.parse(raw); if (s && Array.isArray(s.sent)) memState = s; } } catch {}
  return memState;
}
function writeState(s: SentState) { memState = s; try { store() && store()!.setItem(STORE_KEY, JSON.stringify(s)); } catch {} }

const enabled = () => {
  const w: any = typeof window !== "undefined" ? window : null;
  return !!(w && w.VFP_API_BASE && !(w.VFP_DEMO && w.VFP_DEMO.active));
};
let reporting = false;   // a report that itself throws must not report itself

/** Report one error. Never throws; does nothing in the sandbox. */
export function reportError(err: any, opts: { kind?: CrashKind; componentStack?: string | null; fallbackFrame?: string | null } = {}) {
  if (reporting) return;
  reporting = true;
  try {
    if (!enabled()) return;
    const w: any = window;
    const rep = buildReport(opts.kind || "error", err, { componentStack: opts.componentStack, fallbackFrame: opts.fallbackFrame },
      { ua: navigator.userAgent || "", pathname: location.pathname || "/", width: w.innerWidth || 0, now: Date.now() }, redactList());
    if (isNoise(rep.message, rep.stack) || isThirdParty(rep.stack, opts.fallbackFrame, location.origin)) return;
    const d = decide(readState(), clientFingerprint(rep.message, rep.stack));
    if (!d.send) return;
    writeState(d.next);
    const db = w.VFP_DB;
    if (db && db.clientErrors && db.clientErrors.report) { db.clientErrors.report(rep).catch(() => {}); return; }
    // the data layer never loaded (a crash that early): a signed-out report
    fetch(String(w.VFP_API_BASE).replace(/\/$/, "") + "/client-errors", {
      method: "POST", keepalive: true, headers: { "Content-Type": "application/json" }, body: JSON.stringify(rep),
    }).catch(() => {});
  } catch { /* reporting must never be the second crash */ }
  finally { reporting = false; }
}

let started = false;
/** Listen for uncaught errors and unhandled rejections. Idempotent. */
export function initCrashReporting() {
  if (started || typeof window === "undefined" || typeof window.addEventListener !== "function") return;
  started = true;
  window.addEventListener("error", (ev: any) => {
    // a failed <img>/<script> load fires here too, with no error object: not a crash
    if (!ev || (!ev.error && !ev.message)) return;
    const where = ev.filename ? `${ev.filename}:${ev.lineno || 0}:${ev.colno || 0}` : null;
    reportError(ev.error || ev.message, { kind: "error", fallbackFrame: where });
  });
  window.addEventListener("unhandledrejection", (ev: any) => {
    const r = ev && ev.reason;
    reportError(r instanceof Error ? r : (r && typeof r === "object" && typeof r.message === "string" ? r : String(r)), { kind: "unhandledrejection" });
  });
}

/* the app imports this first (main.tsx), so errors thrown while the other
   modules load are caught too */
initCrashReporting();

export function _resetForTest() { memState = { sent: [], count: 0 }; redactor = null; reporting = false; }
